import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, UserRound } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { API_URL, clientUrl } from '../components/client360/c360Shared';
import { ClientHeader } from '../components/client360/ClientHeader';
import { NextActions } from '../components/client360/NextActions';
import { OrigemCard } from '../components/client360/OrigemCard';
import { ClientJourney } from '../components/client360/ClientJourney';
import { ClientHistory } from '../components/client360/ClientHistory';

const ClientSearch = () => {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/search/global`, { params: { q: q.trim() } });
        const seen = new Set();
        const list = [...(data.clients || []), ...(data.leads || [])].filter((c) => {
          const k = c.nif || c.name; if (!k || seen.has(k)) return false; seen.add(k); return true;
        });
        setRes(list);
      } catch { setRes([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="max-w-xl space-y-3" data-testid="c360-search">
      <h1 className="text-3xl font-bold font-[Outfit] text-[#2D3E6F]">Página do Cliente</h1>
      <p className="text-sm text-slate-500">Pesquise por NIF ou nome para ver tudo sobre o cliente num só sítio.</p>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="NIF ou nome do cliente" className="pl-9" data-testid="c360-search-input" />
      </div>
      {res.map((c, i) => (
        <button key={i} onClick={() => navigate(clientUrl(c.nif, c.name))} data-testid={`c360-search-result-${i}`}
          className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-[#4DD4E8] text-left transition-colors">
          <UserRound className="w-4 h-4 text-[#2D3E6F]" />
          <span className="flex-1 font-medium">{c.name}</span>
          <span className="font-mono text-xs text-slate-500">{c.nif || 'sem NIF'}</span>
        </button>
      ))}
    </div>
  );
};

export default function Client360Page() {
  const [params] = useSearchParams();
  const nif = params.get('nif');
  const name = params.get('name');
  const contractId = params.get('contract_id');
  const leadId = params.get('lead_id');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!nif && !name && !contractId && !leadId) return;
    setError('');
    axios.get(`${API_URL}/clients/360`, { params: { nif: nif || undefined, name: name || undefined, contract_id: contractId || undefined, lead_id: leadId || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => setError((typeof e.response?.data?.detail === 'string' && e.response.data.detail) || 'Erro ao carregar o cliente'));
  }, [nif, name, contractId, leadId]);
  useEffect(() => { setData(null); load(); }, [load]);

  let body;
  if (!nif && !name && !contractId && !leadId) body = <ClientSearch />;
  else if (error) body = <div className="space-y-6"><div className="p-4 rounded-xl bg-rose-50 text-rose-800 text-sm" data-testid="c360-error">{error}</div><ClientSearch /></div>;
  else if (!data) body = <div className="flex justify-center py-20"><div className="spinner" /></div>;
  else body = (
    <div className="space-y-5" data-testid="c360-page">
      <ClientHeader cliente={data.cliente} resumo={data.resumo} />
      <OrigemCard origem={data.origem} />
      <NextActions acoes={data.acoes} cliente={data.cliente} onChanged={load} />
      <ClientJourney percurso={data.percurso} />
      <ClientHistory data={data} />
    </div>
  );
  return <Layout>{body}</Layout>;
}
